// ============================================================
// LinuxTroubleshooting.jsx
//
// A branded, responsive course content page for the
// "Troubleshooting & Recovery" lesson.
//
// Author: ProDev Linux Series
// Features: Diagnostic command sets, recovery procedures,
//           and interactive UI components for junior sysadmins.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ConstructionIcon       from "@mui/icons-material/Construction";
import SearchIcon             from "@mui/icons-material/Search";
import LanIcon                from "@mui/icons-material/Lan";
import RestartAltIcon         from "@mui/icons-material/RestartAlt";
import SecurityIcon           from "@mui/icons-material/Security";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon               from "@mui/icons-material/Link";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import BugReportIcon          from "@mui/icons-material/BugReport";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxTroubleshooting() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Diagnostics · Module 08"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Troubleshooting <span>& Recovery</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Linux provides an arsenal of diagnostic tools to identify root causes. Whether it's resource exhaustion, network failure, or boot errors, the terminal is your primary interface for restoration.
                    </Typography>
                </Box>

                {/* GENERAL DIAGNOSTICS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="System Diagnostics" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 }, mb: 3 }}>
                        Troubleshooting begins with visibility. At <strong style={{ color: BRAND.white }}>ProDevConsult</strong>, we follow a systematic approach: check resource usage first, followed by kernel logs, then service-specific logs.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "$ top / htop",    comment: "Monitor real-time CPU/memory and active processes" },
                        { cmd: "$ free -h",       comment: "Check RAM usage in human-readable format" },
                        { cmd: "$ df -h",         comment: "Inspect disk space and partition health" },
                        { cmd: "$ dmesg",         comment: "View kernel ring buffer messages (hardware/boot info)" },
                        { cmd: "$ journalctl -xe",comment: "Systemd error logs with hints for the latest failures" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* NETWORK ISSUES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LanIcon />} label="Network Connectivity" />
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            { icon: <LanIcon />, title: "Interface Check", description: "Use 'ip a' to verify if your network interface is UP and has an assigned IP address." },
                            { icon: <BugReportIcon />, title: "Reachability", description: "Ping external IPs (like 8.8.8.8) to determine if the issue is DNS-related or routing-related." },
                        ].map((card) => (
                            <Grid item xs={12} md={6} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                    <CodeBlock lines={[
                        { cmd: "$ ping -c 4 8.8.8.8",    comment: "Test external connectivity" },
                        { cmd: "$ nmcli device status",  comment: "Check status of managed network interfaces" },
                        { cmd: "$ sudo systemctl restart NetworkManager", comment: "Reset the network stack" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BOOT & RECOVERY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RestartAltIcon />} label="Boot & Recovery Procedures" />

                    <ExpandableExample title="Filesystem Repair (fsck)" icon={<ConstructionIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>
                            If a filesystem is marked 'dirty' or has metadata corruption, use <code style={{ color: BRAND.goldBright }}>fsck</code> from a live USB or recovery shell:
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo fsck /dev/sda1", comment: "Scan and repair partition sda1" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Permission Fixes" icon={<SecurityIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>
                            Misconfigured permissions often cause 'Access Denied' errors in apps. Reset them using:
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ ls -l /path/to/target",  comment: "Inspect current ownership/bits" },
                            { cmd: "$ sudo chmod 644 config.txt", comment: "Set standard file permissions" },
                            { cmd: "$ sudo chown user:group file", comment: "Reset owner and group" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Broken Package Recovery" icon={<BugReportIcon />}>
                        <CodeBlock lines={[
                            { cmd: "$ sudo apt install --reinstall packagename", comment: "Fix missing library files" },
                            { cmd: "$ sudo rm /var/lib/dpkg/lock*",              comment: "Clear 'locked' database errors" },
                            { cmd: "$ sudo apt --fix-broken install",           comment: "Resolve dependency conflicts" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Troubleshooting Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Analyze CPU and RAM bottlenecks using top/free",
                            "Diagnose network outages using ping and ip tools",
                            "Review system logs using journalctl and dmesg",
                            "Perform filesystem integrity checks with fsck",
                            "Rectify permission errors causing service failures",
                            "Fix broken package managers and lock files",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Technical Support Resources" />
                    <ResourceLink href="https://wiki.archlinux.org/title/Troubleshooting" label="Arch Wiki Troubleshooting" sub="The ultimate encyclopedia for resolving Linux system failures." />
                    <ResourceLink href="https://linuxize.com/post/how-to-recover-from-linux-boot-failure/" label="Linuxize Boot Recovery" sub="Guide for fixing GRUB and kernel panic issues." />
                    <ResourceLink href="https://www.cyberciti.biz/tips/linux-troubleshooting.html" label="nixCraft Master Tips" sub="Advanced terminal hacks for system diagnostics." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Junior Dev Tip:</strong> When a service fails to start, don't just restart it repeatedly. Use <code style={{ color: BRAND.goldBright }}>tail -f /var/log/syslog</code> to watch the log in real-time while you attempt the start — the error message will appear immediately.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
